import { Component, inject, input, computed, output } from '@angular/core';
import { SectionValidationService } from '../../services/section-validation.service';
import { TooltipModule } from 'primeng/tooltip';
import { ValidationTranslatorService } from '../../services/validation-translator.service';

@Component({
  selector: 'app-item-validation-badge',
  templateUrl: './item-validation-badge.component.html',
  standalone: true,
  imports: [TooltipModule]
})
export class ItemValidationBadgeComponent {
  private sectionValidationService = inject(SectionValidationService);
  private validationTranslator = inject(ValidationTranslatorService);

  // Input: section scope and item index
  scope = input.required<'transport' | 'accommodation' | 'activity' | 'meal'>();
  index = input.required<number>();

  // Output: when badge is clicked
  badgeClick = output<{scope: 'transport' | 'accommodation' | 'activity' | 'meal', index: number}>();

  // Computed properties
  issues = computed(() => this.sectionValidationService.getIssuesForItem(this.scope(), this.index()));
  hasIssues = computed(() => this.issues().length > 0);
  errorCount = computed(() => this.issues().filter(issue => issue.severity === 'error').length);
  warningCount = computed(() => this.issues().filter(issue => issue.severity === 'warning').length);

  badgeClasses = computed(() => {
    const baseClasses = 'inline-flex items-center justify-center w-6 h-6 rounded-full text-xs cursor-pointer hover:opacity-80 transition-opacity p-1';
    const errors = this.errorCount();
    const warnings = this.warningCount();

    if (errors > 0) {
      return `${baseClasses} bg-red-100 text-red-600 hover:bg-red-200`;
    } else if (warnings > 0) {
      return `${baseClasses} bg-orange-100 text-orange-600 hover:bg-orange-200`;
    }
    return baseClasses;
  });

  tooltipText = computed(() => {
    const errors = this.errorCount();
    const warnings = this.warningCount();

    if (errors > 0 && warnings > 0) {
      const errorText = this.validationTranslator.getErrorCountText(errors);
      const warningText = this.validationTranslator.getWarningCountText(warnings);
      return `${errorText}, ${warningText}`;
    } else if (errors > 0) {
      return this.validationTranslator.getErrorCountText(errors);
    } else if (warnings > 0) {
      return this.validationTranslator.getWarningCountText(warnings);
    }
    return '';
  });

  detailedTooltipText = computed(() => {
    const issues = this.issues();
    if (issues.length === 0) return '';

    // Format with dashes for better readability
    const messages = issues.map(issue => `- ${this.validationTranslator.translateIssue(issue)}`);
    return messages.join('\n');
  });

  handleClick() {
    this.badgeClick.emit({scope: this.scope(), index: this.index()});
  }
}